/* eslint-disable react/jsx-wrap-multilines */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import HubspotForm from 'react-hubspot-form';
import { Button, Form, Select, Row, Col, Radio } from 'antd';
import Layout from 'components/Layout';
import Meta from 'components/Meta';
import Image from 'components/Image';
import Banner from 'components/Banner';
import Modal from 'components/Modal';
import SectionContact from 'components/SectionContact';
import TrainingArchiveItem from 'components/TrainingArchiveItem';
import withLocation from 'components/WithLocation';
import styled from 'styled-components';
import PlayButtonSvg from 'images/PlayButton.svg';
import USFlagSvg from 'images/USFlag.svg';

import { TraningContainer, TrainingFilter } from 'styles/training';

import trainingsApi from 'utils/trainingsApi';

const { Group } = Radio;

const StyledRow = styled(Row)`
  .ant-col .ant-form-item-control {
    margin-right: 25px;
  }
  .ant-select {
    width: 278px !important;
    height: 50px !important;
    .ant-select-selector {
      height: 100% !important;
    }
    .ant-select-selection-placeholder {
      color: #5f5f5f;
      font-family: Vollkorn;
      font-size: 20px;
      letter-spacing: -0.15px;
      line-height: 28px;
      padding-top: 10px !important;
      padding-left: 19px !important;
    }
    .ant-select-selection-item {
      font-family: Vollkorn;
      font-size: 20px;
      letter-spacing: -0.15px;
      line-height: 28px;
      padding-top: 10px !important;
      padding-left: 19px !important;
    }
  }
  .ant-btn.pink-btn {
    height: 46px !important;
    min-width: 110px !important;
    &:active {
      background-color: #ff4552 !important;
      color: #fff !important;
    }
    &:focus {
      background-color: #ff4552 !important;
      color: #fff !important;
    }
  }
  .reset-btn {
    height: 46px !important;
    outline: none !important;
    border: none;
    box-shadow: none;
    color: #0a090b !important;
    font-weight: 600 !important;
    font-family: 'Proxima Nova' !important;
    font-size: 16px !important;
  }
  @media screen and (max-width: 450px) {
    .ant-select {
      width: 100% !important;
    }
  }

  @media (max-width: 1025px) and (min-width: 768px) {
    .ant-select {
      width: 206px !important;
    }
  }

  display: flex !important;
  flex-flow: row wrap;
`;

const StyledRowLabel = styled(Row)`
  .ant-col {
    color: #5f5f5f;
    width: 278px;
    font-family: Vollkorn;
    font-size: 16px;
    letter-spacing: -0.12px;
    line-height: 22px;
    margin-right: 25px;
  }
  .ant-col:last-child {
    width: auto;
    height: 39px !important;
  }
`;

const StyledGroup = styled(Group)`
  label:not(:first-child) {
    padding-left: 20px !important;
  }
  label {
    color: #5f5f5f !important;
    font-family: Vollkorn !important;
    font-size: 20px !important;
    font-weight: 600;
    .ant-radio-inner {
      width: 20px !important;
      height: 20px !important;
      border-color: #ff4552 !important;
      border-width: 2px;
      &::after {
        top: 4px !important;
        left: 4px !important;
        background-color: #ff4552 !important;
      }
    }
  }
`;

const StyledCol = styled(Col)`
  @media screen and (max-width: 450px) {
    width: 100% !important;
  }
`;

const StyledBanner = styled(Banner)`
  padding: 290px 0 35px 70px !important;
  @media screen and (max-width: 767px) {
    padding: 290px 0 35px 15px !important;
  }
`;

const { Option } = Select;

const TrainingPage = ({ path, search }) => {
  const [form] = Form.useForm();
  const [trainings, setTrainings] = useState([]);
  const [isVideoOpen, setVideoOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [defaultChecked, setDefaultChecked] = useState(true);
  const [disableState, setDisableState] = useState(true);
  const [filterCoursesList, setFilterCoursesList] = useState([]);
  const [filterStatesList, setFilterStatesList] = useState([]);
  const [filterMonthsList, setFilterMonthsList] = useState([
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]);
  const videoRef = useRef();

  function toggleVideoOpen() {
    if (isVideoOpen) videoRef.current.pause();
    else videoRef.current.play();
    setVideoOpen(!isVideoOpen);
  }

  useEffect(() => {
    getTrainingList(search);
    axios.get(trainingsApi.filterList).then(({ data }) => {
      setFilterStatesList(data.states);
      setFilterCoursesList(data.courses);
    });
  }, []);

  const getTrainingList = (params) => {
    setLoading(true);
    axios
      .get(trainingsApi.list, {
        params,
      })
      .then(({ data }) => {
        const trainings = data.trainings;
        setTrainings(
          trainings.sort(
            (a, b) => new Date(a.startDate) - new Date(b.startDate),
          ),
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const submitForm = async () => {
    try {
      const values = await form.validateFields();
      console.log(values);
      setTrainings([]);
      getTrainingList(values);
    } catch (errorInfo) {
      console.log('Failed:', errorInfo);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setTrainings([]);
    getTrainingList({});
    setDefaultChecked(true);
    setDisableState(false);
  };

  const checkRadioButton = (event) => {
    if (event.target.value === 'online') {
      form.resetFields(['state']);
      setDisableState(true);
    } else {
      setDisableState(false);
    }
    if (event.target.value !== 'all') {
      setDefaultChecked(false);
    }
    submitForm();
  };

  return (
    <>
      <Meta title="Professional Information Technology [IT] Training & Certification Courses"
        description="ATC trains professionals in management & IT through instructor-led online training. View our Process, Cloud & Technology training courses." />
      <Layout
        background="#ffffff"
        path={path}
        renderOutsideScrollContainer={
          <Modal isOpen={isVideoOpen} toggle={toggleVideoOpen}>
            <div className="center">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video controls ref={videoRef}>
                <source
                  src="/what-makes-our-training-superior.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </Modal>
        }
      >
        {/* eslint-disable */}
        <StyledBanner
          backgroundSrc="training/training-bg.jpg"
          title={
            <>
              Propel your career
              <br />
              to the next level
              <span className="pink">.</span>
            </>
          }
          customCSS={`
            &,
            &::before,
            &::after {
              background-position: 90% 0 !important;
            }
            @media only screen and (max-width: 767px) {
              &,
              &::before,
              &::after {
                background-position: 90% 0 !important;
              }
            }
          `}
          className="darken-image"
        >
          {/* eslint-enable */}
          <p
            className="white-txt"
            style={{
              maxWidth: 400,
            }}
          >
            We&apos;re commited to learning and adapting to the industry
            standards and staying ahead of the curve.
          </p>
          <button
            type="button"
            onClick={toggleVideoOpen}
            css={`
              display: inline-block;
              font-size: 18px;
              letter-spacing: 0.2px;
              line-height: 28px;
              margin-top: 75px;
              color: #ffffff;
              background: unset;
              border: 0;
              outline: 0;

              &:hover {
                color: #ff4552;
              }
            `}
          >
            <div style={{ display: 'flex' }}>
              <PlayButtonSvg />
              <span style={{ padding: 12 }}> Play Video</span>
            </div>
          </button>
        </StyledBanner>

        <TraningContainer>
          <section className="d-none">
            <div className="container">
              <div className="training-srch-area">
                <input
                  type="text"
                  className="training-srch-bar"
                  placeholder="Type your search here"
                />
                <button type="submit" className="training-srch-btn">
                  <i className="far fa-search" />
                </button>
              </div>
              <div className="training-filter-area">
                <div className="row flex-row-reverse">
                  <div className="col-md-6">
                    <div className="single-filter right-single-filter">
                      <p>
                        Recent:&nbsp;
                        <a href="#!">
                          <i className="far fa-angle-down" />
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="single-filter">
                      <p>
                        Completed:&nbsp;
                        <a href="#!">
                          All&nbsp;
                          <i className="far fa-angle-down" />
                        </a>
                      </p>
                    </div>
                    <div className="single-filter">
                      <p>
                        Location:&nbsp;
                        <a href="#!">
                          All&nbsp;
                          <i className="far fa-angle-down" />
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <TrainingFilter>
            <div className="flag-container">
              <USFlagSvg></USFlagSvg>
            </div>
            <div className="container">
              <div className="filter-title">Training Calendar</div>
              <div className="form-container">
                <Form form={form}>
                  <StyledRow style={{ marginBottom: 10 }}>
                    <Form.Item name="location">
                      <StyledGroup onChange={() => checkRadioButton(event)}>
                        <Radio value="all">All</Radio>
                        <Radio
                          className={defaultChecked ? 'ant-radio-checked' : ''}
                          value="online">Online</Radio>
                        <Radio value="inperson">In-Person</Radio>
                      </StyledGroup>
                    </Form.Item>
                  </StyledRow>
                  <StyledRow>
                    <StyledCol>
                      <StyledRowLabel>
                        <Col>Course</Col>
                      </StyledRowLabel>
                      <Form.Item name="courseName">
                        <Select placeholder="Select Course" id="state">
                          {filterCoursesList.map((elem) => (
                            <Option value={elem}>{elem}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </StyledCol>
                    <StyledCol>
                      <StyledRowLabel>
                        <Col>State</Col>
                      </StyledRowLabel>
                      <Form.Item name="state">
                        <Select
                          placeholder="Select State"
                          disabled={disableState}
                        >
                          {filterStatesList.map((elem) => (
                            <Option value={elem}>{elem}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </StyledCol>
                    <StyledCol>
                      <StyledRowLabel>
                        <Col>Month</Col>
                      </StyledRowLabel>
                      <Form.Item name="monthName">
                        <Select placeholder="Select Month">
                          {filterMonthsList.map((elem) => (
                            <Option value={elem}>{elem}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </StyledCol>
                    <StyledCol className="btn-col">
                      <StyledRowLabel>
                        <Col></Col>
                      </StyledRowLabel>
                      <Button
                        htmlType="submit"
                        className="pink-btn"
                        onClick={submitForm}
                      // style={{ minWidth: '90px' }}
                      >
                        Apply
                      </Button>
                      <Button onClick={resetForm} className="reset-btn">
                        Reset
                      </Button>
                    </StyledCol>
                  </StyledRow>
                </Form>
              </div>
            </div>
          </TrainingFilter>
          <section className="training-modules-main">
            <div className="container">
              {trainings.length ? (
                <div className="training-count">
                  Showing {trainings.length} trainings
                </div>
              ) : null}
              {trainings.length > 0 ? (
                Array.from(
                  { length: trainings.length },
                  (v, k) => k + 1,
                ).map((i) => (
                  <TrainingArchiveItem key={i} id={i} data={trainings[i - 1]} />
                ))
              ) : (
                <div className="center" style={{ padding: '50px 0' }}>
                  <p>{loading ? 'Loading...' : 'No trainings found.'} </p>
                </div>
              )}
            </div>
          </section>

          <Image
            type="background"
            tag="section"
            src="training/training-laptop.jpg"
            className="training-new-block darken-image"
          >
            <div className="container">
              <div className="training-new-area">
                <p>Subscribe!</p>
                <h3>
                  Get monthly updates about new articles, webinars, and
                  trainings
                  <span className="pink">.</span>
                </h3>
                <HubspotForm
                  portalId="2117583"
                  formId="cff9b84f-4a28-481d-9e00-fc1e23d20719"
                  submitButtonClass="pink-btn"
                />
              </div>
            </div>
          </Image>
        </TraningContainer>

        <SectionContact title="Interested in Speaking?" cta="Get Involved" />
      </Layout>
    </>
  );
};

TrainingPage.propTypes = {
  path: PropTypes.string.isRequired,
};

export default withLocation(TrainingPage);
